import React, { useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useScroll,
  useTransform,
  useSpring
} from 'framer-motion';
import {
  ChevronDown,
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  X,
  CheckCircle2,
  SlidersHorizontal,
  ChevronRight,
  Building2,
  ShieldCheck,
  Menu as MenuIcon
} from 'lucide-react';

import brands from './data/brands.json';

const EASE = [0.22, 1, 0.36, 1];

/* ========================================================
   REUSABLE MOTION COMPONENTS (FROM BIG PROMPT REFERENCE)
   ======================================================== */
function Reveal({ children, className = '', delay = 0, as = 'div', style = {} }) {
  const Component = motion[as] || motion.div;
  return (
    <Component
      className={className}
      style={style}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.75, delay, ease: EASE }}
    >
      {children}
    </Component>
  );
}

function Magnetic({ children, className = '', onClick, ...props }) {
  const x = useSpring(0, { stiffness: 220, damping: 20 });
  const y = useSpring(0, { stiffness: 220, damping: 20 });

  return (
    <motion.div
      className={className}
      style={{ x, y, display: 'inline-block' }}
      onPointerMove={e => {
        if (e.pointerType !== 'mouse') return;
        const rect = e.currentTarget.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        x.set((e.clientX - cx) * 0.28);
        y.set((e.clientY - cy) * 0.28);
      }}
      onPointerLeave={() => {
        x.set(0);
        y.set(0);
      }}
      onClick={onClick}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/* ========================================================
   HEADER & NAVIGATION
   ======================================================== */
function Header({ onConsultClick }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="slick-header">
      <div className="slick-header-inner">
        <a href="#home" className="slick-brand" aria-label="NexusPrime Home">
          <img src="./Asset/img/logo.png" alt="NexusPrime Logo" className="slick-brand-logo" />
          <span className="slick-brand-title">
            nexusprime<span>®</span>
          </span>
        </a>

        <nav className="slick-nav">
          <a href="#story">Our Story</a>
          <a href="#experience">Campus Experience</a>
          <a href="#elite">Enterprise Elite</a>
          <a href="#ecosystem">Ecosystem</a>
          <a href="#transformation">Cafeteria Redesign</a>
          <a href="#contact">Contact</a>
        </nav>

        <div className="slick-header-actions">
          <Magnetic onClick={onConsultClick}>
            <button className="btn-nav-tasting">
              Schedule Tasting <ArrowUpRight size={15} />
            </button>
          </Magnetic>
          <button
            className="mobile-nav-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            style={{
              background: '#fff',
              borderBottom: '1px solid var(--border)',
              padding: '18px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              overflow: 'hidden'
            }}
          >
            {['story', 'experience', 'elite', 'ecosystem', 'transformation', 'contact'].map(sec => (
              <a
                key={sec}
                href={`#${sec}`}
                onClick={() => setMobileOpen(false)}
                style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'var(--indigo)',
                  textTransform: 'capitalize'
                }}
              >
                {sec}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ========================================================
   SLIDE 1: CINEMATIC FULLSCREEN HERO (SCROLL PARALLAX)
   ======================================================== */
function CinemaHero({ onConsultClick }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, 160]);
  const bgScale = useTransform(scrollY, [0, 600], [1, 1.08]);
  const contentY = useTransform(scrollY, [0, 500], [0, 60]);
  const contentOpacity = useTransform(scrollY, [0, 450], [1, 0.2]);

  return (
    <section id="home" className="cinema-hero">
      <motion.img
        src="./Asset/img/tandoori_platter.jpg"
        alt="NexusPrime Culinary Spread"
        className="cinema-hero-bg"
        style={{ y: bgY, scale: bgScale }}
      />
      <div className="cinema-hero-overlay" />

      <motion.div
        className="cinema-hero-content"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.img
          src="./Asset/img/logo.png"
          alt="NexusPrime"
          className="cinema-hero-logo"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: EASE }}
        />
        <motion.div
          className="cinema-hero-wordmark"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
        >
          nexusprime
        </motion.div>
        <motion.h1
          className="cinema-hero-headline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.2, ease: EASE }}
        >
          India’s #1 corporate cafeteria platform
        </motion.h1>
        <motion.p
          className="cinema-hero-subtitle"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.3, ease: EASE }}
        >
          Experience fast, chef-crafted corporate dining across your campuses
        </motion.p>

        <motion.div
          className="cinema-hero-btns"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.4, ease: EASE }}
        >
          <Magnetic onClick={onConsultClick}>
            <button className="btn-cinema-primary">
              Schedule a Tasting <ArrowUpRight size={17} />
            </button>
          </Magnetic>
          <Magnetic>
            <a href="#story" className="btn-cinema-glass">
              Explore Offerings ↓
            </a>
          </Magnetic>
        </motion.div>

        <motion.a
          href="#story"
          className="cinema-scroll-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.8 }}
        >
          <span>Scroll down</span>
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <ChevronDown size={16} />
          </motion.div>
        </motion.a>
      </motion.div>
    </section>
  );
}

/* ========================================================
   SLIDE 2: FLOATING FOOD SHOWCASE (CONTINUOUS MOTION DRIFT)
   ======================================================== */
function FloatingFoodShowcase() {
  return (
    <section id="story" className="floating-food-section">
      {/* Decorative Wavy Doodle Line */}
      <svg className="doodle-line-bg" viewBox="0 0 1440 600" fill="none">
        <path
          d="M-50,200 C300,50 450,450 720,250 C1000,50 1150,450 1500,200"
          stroke="#FF6B1A"
          strokeWidth="1.6"
          strokeOpacity="0.22"
          strokeDasharray="8 8"
        />
        <path
          d="M-50,400 C200,550 500,150 800,380 C1100,550 1300,200 1500,420"
          stroke="#1A237E"
          strokeWidth="1.4"
          strokeOpacity="0.14"
        />
      </svg>

      {/* Floating Food Card 1 (Dosa) - Infinite Sine Drift */}
      <motion.div
        className="floating-food-card pos-food-1"
        animate={{
          y: [0, -15, 0],
          rotate: [-6, -3, -6]
        }}
        transition={{
          repeat: Infinity,
          duration: 5.5,
          ease: "easeInOut"
        }}
      >
        <img src="./Asset/img/float_dosa.png" alt="Artisanal Live Dosa" />
      </motion.div>

      {/* Floating Food Card 2 (Burger) - Infinite Sine Drift */}
      <motion.div
        className="floating-food-card pos-food-2"
        animate={{
          y: [0, 16, 0],
          rotate: [7, 10, 7]
        }}
        transition={{
          repeat: Infinity,
          duration: 6.2,
          ease: "easeInOut"
        }}
      >
        <img src="./Asset/img/float_burger.png" alt="Gourmet Burger" />
      </motion.div>

      {/* Floating Food Card 3 (Pizza) - Infinite Sine Drift */}
      <motion.div
        className="floating-food-card pos-food-3"
        animate={{
          y: [0, -13, 0],
          rotate: [-4, -1, -4]
        }}
        transition={{
          repeat: Infinity,
          duration: 4.8,
          ease: "easeInOut"
        }}
      >
        <img src="./Asset/img/float_pizza.png" alt="Artisanal Woodfired Pizza" />
      </motion.div>

      {/* Floating Food Card 4 (Steamed Dim Sum) - Infinite Sine Drift */}
      <motion.div
        className="floating-food-card pos-food-4"
        animate={{
          y: [0, 14, 0],
          rotate: [5, 2, 5]
        }}
        transition={{
          repeat: Infinity,
          duration: 5.2,
          ease: "easeInOut"
        }}
      >
        <img src="./Asset/img/float_dimsum.png" alt="Steamed Dim Sum" />
      </motion.div>

      {/* Center Content with Reveal */}
      <div className="floating-food-inner">
        <Reveal>
          <h2 className="floating-food-title">Better food for every workplace</h2>
          <p className="floating-food-subtext">
            For over a decade, we’ve enabled enterprise teams to discover chef-crafted meals, delivered fresh to corporate campuses.
          </p>
        </Reveal>

        {/* 3-Stat Pill Card with Reveal */}
        <Reveal delay={0.2}>
          <div className="stat-pill-container">
            <div className="stat-pill-item">
              <div className="stat-pill-icon-wrap">
                <Building2 size={22} color="#FF6B1A" />
              </div>
              <div>
                <div className="stat-pill-num">250+</div>
                <div className="stat-pill-label">corporate campuses</div>
              </div>
            </div>

            <div className="stat-pill-divider" />

            <div className="stat-pill-item">
              <div className="stat-pill-icon-wrap">
                <MapPin size={22} color="#FF6B1A" />
              </div>
              <div>
                <div className="stat-pill-num">Kolkata • Durgapur • Bardhaman</div>
                <div className="stat-pill-label">regional production hubs</div>
              </div>
            </div>

            <div className="stat-pill-divider" />

            <div className="stat-pill-item">
              <div className="stat-pill-icon-wrap">
                <ShieldCheck size={22} color="#FF6B1A" />
              </div>
              <div>
                <div className="stat-pill-num">99.4%</div>
                <div className="stat-pill-label">hygiene audit rating</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ========================================================
   SLIDE 3: PHONE MOCKUP WITH STAGGERED FLOATING PILLS
   ======================================================== */
function AppFeatureSection() {
  return (
    <section id="experience" className="app-feature-section">
      <div className="app-feature-inner">
        <Reveal>
          <h2 className="app-feature-title">What’s waiting for your campus?</h2>
          <p className="app-feature-sub">
            Our turnkey cafeteria ecosystem delivers seamless corporate dining experiences like never before
          </p>
        </Reveal>

        <div className="phone-mockup-wrap">
          {/* Floating Left Tiles with Staggered Sine Drift */}
          <motion.div
            className="floating-tile tile-left-top"
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3.8, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_salad.png" alt="Healthy & Keto" className="tile-thumb-img" />
            <span className="tile-label">Healthy &amp; Keto</span>
          </motion.div>

          <motion.div
            className="floating-tile tile-left-mid"
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 4.2, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_dosa.png" alt="Live Dosa Bar" className="tile-thumb-img" />
            <span className="tile-label">Live Dosa Bar</span>
          </motion.div>

          <motion.div
            className="floating-tile tile-left-bot"
            animate={{ y: [0, -7, 0] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_burger.png" alt="Gourmet Burgers" className="tile-thumb-img" />
            <span className="tile-label">Gourmet Burgers</span>
          </motion.div>

          {/* Center Phone Device with Gentle Entrance */}
          <motion.div
            className="phone-device"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="phone-notch" />
            <div className="phone-screen">
              <img
                src="./Asset/img/smart_foodcourt.jpg"
                alt="NexusPrime Smart Foodcourt"
                className="phone-screen-img"
              />
              <div className="phone-screen-banner">
                <div className="phone-banner-title">Smart Campus Dining</div>
                <div className="phone-banner-sub">Sub-60s contactless pickup</div>
              </div>
            </div>
          </motion.div>

          {/* Floating Right Tiles with Staggered Sine Drift */}
          <motion.div
            className="floating-tile tile-right-top"
            animate={{ y: [0, 7, 0] }}
            transition={{ repeat: Infinity, duration: 4.0, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_coffee.png" alt="Artisan Coffee" className="tile-thumb-img" />
            <span className="tile-label">Artisan Coffee</span>
          </motion.div>

          <motion.div
            className="floating-tile tile-right-mid"
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3.6, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_dimsum.png" alt="Dim Sum Station" className="tile-thumb-img" />
            <span className="tile-label">Dim Sum Station</span>
          </motion.div>

          <motion.div
            className="floating-tile tile-right-bot"
            animate={{ y: [0, 9, 0] }}
            transition={{ repeat: Infinity, duration: 4.4, ease: "easeInOut" }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img src="./Asset/img/float_pizza.png" alt="Woodfired Pizza" className="tile-thumb-img" />
            <span className="tile-label">Woodfired Pizza</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================
   SLIDE 4: DARK REGAL CURVED BANNER (ENTERPRISE ELITE)
   ======================================================== */
function GoldCurvedSection() {
  return (
    <section id="elite" className="gold-curved-section">
      {/* Floating 3D Gold Medallions with gentle rotation and vertical drift */}
      <motion.div
        className="gold-coin gold-coin-1"
        animate={{ y: [0, -12, 0], rotate: [-15, -8, -15] }}
        transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
      >
        <img src="./Asset/img/gold_coin.png" alt="Elite Medallion" className="gold-coin-img" />
      </motion.div>
      <motion.div
        className="gold-coin gold-coin-2"
        animate={{ y: [0, 14, 0], rotate: [20, 26, 20] }}
        transition={{ repeat: Infinity, duration: 6.8, ease: "easeInOut" }}
      >
        <img src="./Asset/img/gold_coin.png" alt="Elite Medallion" className="gold-coin-img" />
      </motion.div>
      <motion.div
        className="gold-coin gold-coin-3"
        animate={{ y: [0, -10, 0], rotate: [-10, -4, -10] }}
        transition={{ repeat: Infinity, duration: 5.4, ease: "easeInOut" }}
      >
        <img src="./Asset/img/gold_coin.png" alt="Elite Medallion" className="gold-coin-img" />
      </motion.div>

      <div className="gold-inner">
        <Reveal>
          <div className="gold-brand-label">nexusprime</div>
          <h2 className="gold-crown-title">ELITE</h2>
          <p className="gold-tagline">
            India’s Top Workplace Dining Program for Enterprise Campuses
          </p>
          <div className="gold-benefits-header">ENTERPRISE PRIVILEGES &amp; COMPLIANCE</div>
        </Reveal>

        <div className="gold-benefits-grid">
          {[
            {
              title: 'FSSAI Grade A+ & ISO 22000',
              desc: 'Comprehensive microbiological HACCP food safety protocols'
            },
            {
              title: 'Sub-60s Counter Throughput',
              desc: 'Zero queue bottlenecks with RFID employee badge tap'
            },
            {
              title: '100% Eco Directive',
              desc: 'Zero single-use plastic directive with compostable packaging'
            }
          ].map((b, i) => (
            <motion.div
              key={i}
              className="gold-benefit-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.65, delay: i * 0.15, ease: EASE }}
              whileHover={{ y: -6, backgroundColor: 'rgba(255, 255, 255, 0.09)' }}
            >
              <img src="./Asset/img/gold_coin.png" alt="Standard" className="gold-card-badge-img" />
              <div>
                <div className="gold-card-title">{b.title}</div>
                <div className="gold-card-desc">{b.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================================================
   SLIDE 5: 4-CARD ECOSYSTEM (WITH 3D BADGES & HOVER MOTION)
   ======================================================== */
function EcosystemSection({ onSelectOffer }) {
  const offerings = [
    {
      theme: 'eco-theme-orange',
      img: './Asset/img/eco_kitchen.png',
      title: 'Turnkey Kitchens',
      desc: 'Full on-site commercial kitchen setup & daily executive chef brigade',
      link: 'Explore setup ›'
    },
    {
      theme: 'eco-theme-yellow',
      img: './Asset/img/eco_drops.png',
      title: 'Daily Hot Drops',
      desc: 'Central base kitchen bulk cooked meal logistics in temperature-controlled fleet',
      link: 'Explore supply ›'
    },
    {
      theme: 'eco-theme-purple',
      img: './Asset/img/eco_kiosk.png',
      title: 'Multi-Brand Kiosks',
      desc: 'Curated live food courts & rotating artisanal branded stations',
      link: 'Explore kiosks ›'
    },
    {
      theme: 'eco-theme-red',
      img: './Asset/img/eco_pos.png',
      title: 'xCliq Smart POS',
      desc: 'Contactless touchscreen ordering & RFID employee badge tap integration',
      link: 'Explore technology ›'
    }
  ];

  return (
    <section id="ecosystem" className="ecosystem-section">
      <div className="ecosystem-inner">
        <Reveal>
          <h2 className="ecosystem-brand-title">our ecosystem</h2>
          <p className="ecosystem-subtext">POWERING WORKPLACE CULINARY EXCELLENCE</p>
        </Reveal>

        <div className="ecosystem-grid">
          {offerings.map((item, idx) => (
            <motion.div
              key={idx}
              className={`ecosystem-card ${item.theme}`}
              onClick={() => onSelectOffer(item)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, delay: idx * 0.1, ease: EASE }}
              whileHover={{
                y: -10,
                scale: 1.02,
                transition: { duration: 0.25 }
              }}
            >
              <motion.div
                className="eco-app-icon"
                whileHover={{ rotate: 5, scale: 1.08 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img src={item.img} alt={item.title} className="eco-app-img" />
              </motion.div>
              <h3 className="eco-card-title">{item.title}</h3>
              <p className="eco-card-desc">{item.desc}</p>
              <span className="eco-card-link">
                {item.link}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================================================
   BEFORE / AFTER TRANSFORMATION SECTION
   ======================================================== */
function TransformationSection() {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = clientX => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos(Math.round((x / rect.width) * 100));
  };

  return (
    <section id="transformation" className="transform-section">
      <div className="transform-container">
        <Reveal>
          <div className="transform-header">
            <h2 className="transform-title">Workplace Cafeteria Transformation</h2>
            <p className="transform-sub">
              Drag the slider to compare traditional drab office canteens with NexusPrime modern smart dining hubs
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            ref={containerRef}
            className="slider-box"
            onMouseDown={() => { isDragging.current = true; }}
            onMouseUp={() => { isDragging.current = false; }}
            onMouseLeave={() => { isDragging.current = false; }}
            onMouseMove={e => {
              if (isDragging.current) handleMove(e.clientX);
            }}
            onTouchMove={e => {
              if (e.touches && e.touches[0]) handleMove(e.touches[0].clientX);
            }}
          >
            {/* Outdated Canteen (Left Background) */}
            <img
              src="./Asset/img/traditional_canteen.jpg"
              alt="Traditional Office Canteen"
              className="slider-img"
            />
            <span className="badge-left-lbl">BEFORE · TRADITIONAL CANTEEN</span>

            {/* Smart Foodcourt (Right Overlay) */}
            <div
              className="slider-overlay"
              style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
            >
              <img
                src="./Asset/img/smart_foodcourt.jpg"
                alt="NexusPrime Modern Smart Foodcourt"
                className="slider-img"
              />
              <span className="badge-right-lbl">AFTER · NEXUSPRIME SMART FOODCOURT</span>
            </div>

            {/* Divider & Knob */}
            <div className="slider-bar" style={{ left: `${pos}%` }}>
              <div className="slider-knob">
                <SlidersHorizontal size={18} />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ========================================================
   CONTINUOUS SCROLLING BRAND MARQUEE RAIL (FROM BIG PROMPT)
   ======================================================== */
function PartnersBand() {
  const marqueeBrands = [...brands, ...brands];

  return (
    <div className="partners-band">
      <Reveal>
        <p>Trusted in the cafeterias of leading enterprises across India</p>
      </Reveal>
      <div className="partners-track-wrapper">
        <div className="partners-track">
          {marqueeBrands.map((b, idx) => (
            <div key={idx} className="partner-logo-item">
              <img src={b.logo} alt={b.name} title={b.name} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   CONTACT & CONSULTATION FORM (2 COLUMNS)
   ======================================================== */
function ContactBlock({ onToast }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    message: ''
  });
  const [sent, setSent] = useState(false);

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    onToast(`Copied ${label} to clipboard!`);
  };

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const text = encodeURIComponent(
      `Hello NexusPrime!\n\n` +
      `Corporate Cafeteria Tasting / Consultation Request:\n` +
      `Name: ${formData.firstName} ${formData.lastName}\n` +
      `Company: ${formData.company}\n` +
      `Email: ${formData.email}\n` +
      `Requirements: ${formData.message}`
    );
    window.open(`https://wa.me/917001863063?text=${text}`, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section id="contact" className="contact-block">
      <div className="contact-container">
        {/* Left Column with Reveal */}
        <Reveal className="contact-left">
          <h3>Ready to Transform Your Workplace Dining?</h3>
          <p>
            Whether you are looking to upgrade an existing office cafeteria, establish a turnkey live kitchen, or schedule a leadership tasting, our team is here to help.
          </p>

          <div className="contact-channel-list">
            <div className="contact-channel-item">
              <div className="channel-icon-pill">
                <Mail size={20} />
              </div>
              <div className="channel-text-group">
                <span className="channel-type">Direct Email</span>
                <span className="channel-val" onClick={() => copy('admin@nexus-prime.in', 'email')}>
                  admin@nexus-prime.in
                </span>
                <span className="channel-val" onClick={() => copy('opsconnect@nexus-prime.in', 'email')}>
                  opsconnect@nexus-prime.in
                </span>
              </div>
            </div>

            <div className="contact-channel-item">
              <div className="channel-icon-pill">
                <Phone size={20} />
              </div>
              <div className="channel-text-group">
                <span className="channel-type">Phone Lines</span>
                <span className="channel-val" onClick={() => copy('+91 7001863063', 'phone')}>
                  +91 7001863063
                </span>
                <span className="channel-val" onClick={() => copy('+91 6296558710', 'phone')}>
                  +91 6296558710
                </span>
                <span className="channel-val" onClick={() => copy('+91 8335849434', 'phone')}>
                  +91 8335849434
                </span>
              </div>
            </div>

            <div className="contact-channel-item">
              <div className="channel-icon-pill">
                <MapPin size={20} />
              </div>
              <div className="channel-text-group">
                <span className="channel-type">Regional Operations &amp; HQ</span>
                <span className="channel-val">
                  Operations: Kolkata • Durgapur • Bardhaman
                </span>
                <span style={{ fontSize: '13px', color: 'var(--ink-muted)', marginTop: '2px' }}>
                  Headquarters: Durgapur, West Bengal
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Right Column Form with Reveal */}
        <Reveal delay={0.15}>
          <form className="contact-form-wrap" onSubmit={handleSubmit}>
            <div className="form-duo-row">
              <div className="form-input-unit">
                <label>First Name</label>
                <input
                  name="firstName"
                  required
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
              <div className="form-input-unit">
                <label>Last Name</label>
                <input
                  name="lastName"
                  required
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-input-unit">
              <label>Corporate Email</label>
              <input
                type="email"
                name="email"
                required
                placeholder="john@company.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="form-input-unit">
              <label>Company / Campus Name</label>
              <input
                name="company"
                required
                placeholder="Acme Technologies Ltd"
                value={formData.company}
                onChange={handleChange}
              />
            </div>

            <div className="form-input-unit">
              <label>Requirements / Campus Headcount</label>
              <textarea
                name="message"
                rows={4}
                required
                placeholder="Mention campus location, estimated daily meals, cafeteria setup requirement..."
                value={formData.message}
                onChange={handleChange}
              />
            </div>

            <Magnetic>
              <button type="submit" className="btn-submit-tasting">
                Schedule Tasting &amp; Proposal <ArrowUpRight size={17} />
              </button>
            </Magnetic>

            {sent && (
              <p style={{ marginTop: '14px', fontSize: '14px', color: '#16a34a', fontWeight: 600 }}>
                ✓ Consultation request formatted in WhatsApp!
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}

/* ========================================================
   SLICK STRUCTURED FOOTER
   ======================================================== */
function SlickFooter({ onJoin, onLegal }) {
  return (
    <footer className="slick-footer">
      <div className="slick-footer-inner">
        <div className="footer-top-row">
          <a href="#home" className="slick-brand">
            <img src="./Asset/img/logo.png" alt="NexusPrime" className="slick-brand-logo" />
            <span className="slick-brand-title">
              nexusprime<span>®</span>
            </span>
          </a>

          <div className="footer-badges">
            <span className="footer-badge-pill">Pan-India Logistics</span>
            <span className="footer-badge-pill">FSSAI Grade A+</span>
            <button className="btn-join-pill" onClick={onJoin}>
              Careers / Join Us ↗
            </button>
          </div>
        </div>

        <div className="footer-quad-grid">
          <div className="footer-quad-col">
            <h4>ABOUT NEXUSPRIME</h4>
            <div className="footer-quad-links">
              <a href="#story">Culinary Philosophy</a>
              <a href="#elite">FSSAI Hygiene Audits</a>
              <a href="#ecosystem">Enterprise SLAs</a>
              <span onClick={onJoin}>Join Our Culinary Team</span>
            </div>
          </div>

          <div className="footer-quad-col">
            <h4>ECOSYSTEM</h4>
            <div className="footer-quad-links">
              <a href="#ecosystem">Turnkey On-Site Kitchens</a>
              <a href="#ecosystem">Central Base Drops</a>
              <a href="#experience">Multi-Brand Kiosks</a>
              <a href="#ecosystem">xCliq Smart POS Systems</a>
            </div>
          </div>

          <div className="footer-quad-col">
            <h4>LOCATIONS</h4>
            <div className="footer-quad-links">
              <a href="#contact">Kolkata Tech Parks</a>
              <a href="#contact">Durgapur Headquarters</a>
              <a href="#contact">Bardhaman Fleet Hubs</a>
              <a href="#contact">Campus Tasting Request</a>
            </div>
          </div>

          <div className="footer-quad-col">
            <h4>CONTACT &amp; HQ</h4>
            <div className="footer-quad-links">
              <a href="mailto:admin@nexus-prime.in">admin@nexus-prime.in</a>
              <a href="mailto:opsconnect@nexus-prime.in">opsconnect@nexus-prime.in</a>
              <a href="tel:+917001863063">+91 7001863063</a>
              <span>Durgapur, West Bengal (HQ)</span>
            </div>
          </div>
        </div>

        <div className="footer-bottom-copy">
          <p>© 2026 NexusPrime Technologies Pvt Ltd. All rights reserved.</p>
          <div className="footer-terms-links">
            <span onClick={() => onLegal('Privacy Policy')}>Privacy Policy</span>
            <span onClick={() => onLegal('Terms of Service')}>Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ========================================================
   ANIMATED MODAL (ANIMATE PRESENCE)
   ======================================================== */
function Modal({ title, content, onClose }) {
  return (
    <AnimatePresence>
      {title && (
        <motion.div
          className="slick-modal-overlay"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="slick-modal-card"
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 24 }}
            transition={{ duration: 0.3, ease: EASE }}
          >
            <button className="slick-modal-close" onClick={onClose} aria-label="Close modal">
              <X size={18} />
            </button>
            <div className="slick-modal-body">
              <h2 className="slick-modal-title">{title}</h2>
              <div className="slick-modal-text">
                {content}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ========================================================
   MAIN APPLICATION
   ======================================================== */
export default function App() {
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  // Top reading-progress bar linked to scroll
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const showToast = msg => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleJoin = () => {
    setModal({
      title: 'Join Team NexusPrime',
      content: (
        <div>
          <p>
            We are actively looking for passionate culinary professionals, cafeteria operations managers, executive nutritionists, and logistics specialists across Kolkata, Durgapur, and Bardhaman.
          </p>
          <p style={{ marginTop: '12px' }}>
            Please email your resume to <strong>careers@nexus-prime.in</strong> or WhatsApp us directly at <strong>+91 7001863063</strong>.
          </p>
        </div>
      )
    });
  };

  const handleLegal = type => {
    if (type === 'Privacy Policy') {
      setModal({
        title: 'Privacy Policy',
        content: (
          <div>
            <p>
              At NexusPrime, we respect your corporate privacy. Any contact and consultation details submitted through our website are strictly used to schedule cafeteria tastings, service proposals, and facility audits.
            </p>
            <p style={{ marginTop: '12px' }}>
              We never share or sell company contact information to any third parties. All inquiries are securely stored and handled under strict data protection practices.
            </p>
          </div>
        )
      });
    } else {
      setModal({
        title: 'Terms of Service',
        content: (
          <div>
            <p>
              NexusPrime provides turnkey corporate cafeteria management, multi-brand kiosk curation, and daily catering services across India under service-level agreements (SLAs).
            </p>
            <p style={{ marginTop: '12px' }}>
              All food preparation complies with FSSAI Food Safety Standards, ISO 22000, and HACCP protocols. For corporate contract specifications, please contact admin@nexus-prime.in.
            </p>
          </div>
        )
      });
    }
  };

  const handleSelectOffer = offer => {
    setModal({
      title: offer.title,
      content: (
        <div>
          <p>{offer.desc}</p>
          <p style={{ marginTop: '14px', fontWeight: 600, color: 'var(--indigo)' }}>
            Available for enterprise campuses in Kolkata, Durgapur, and Bardhaman.
          </p>
          <button
            className="btn-submit-tasting"
            style={{ marginTop: '20px', width: '100%' }}
            onClick={() => {
              setModal(null);
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Configure for Your Campus
          </button>
        </div>
      )
    });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <MotionConfig transition={{ ease: EASE, duration: 0.65 }}>
      {/* Scroll-linked progress bar at the very top */}
      <motion.div className="scroll-progress-bar" style={{ scaleX }} />

      <Header onConsultClick={scrollToContact} />

      <main>
        <CinemaHero onConsultClick={scrollToContact} />
        <FloatingFoodShowcase />
        <AppFeatureSection />
        <GoldCurvedSection />
        <EcosystemSection onSelectOffer={handleSelectOffer} />
        <TransformationSection />
        <PartnersBand />
        <ContactBlock onToast={showToast} />
      </main>

      <SlickFooter onJoin={handleJoin} onLegal={handleLegal} />

      <AnimatePresence>
        {toast && (
          <motion.div
            className="slick-toast"
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.92 }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <CheckCircle2 size={16} color="#fff" />
            <span>{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        title={modal?.title}
        content={modal?.content}
        onClose={() => setModal(null)}
      />
    </MotionConfig>
  );
}

createRoot(document.getElementById('root')).render(<App />);
